import { supabase } from './supabase';
import { API_CONFIG } from '../config/api';

const SUPABASE_URL = API_CONFIG.SUPABASE_URL;

// 标准化响应接口
interface StandardResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  metadata?: {
    total?: number;
    page?: number;
    limit?: number;
  };
}

// 通用函数调用方法
const callFunction = async <T>(
  functionName: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'POST',
  body?: any,
  queryParams?: Record<string, string>
): Promise<StandardResponse<T>> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    let url = `${API_CONFIG.FUNCTIONS_URL}/${functionName}`;
    if (queryParams) {
      const params = new URLSearchParams(queryParams);
      url += `?${params.toString()}`;
    }

    const response = await fetch(url, {
      method,
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json',
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Function ${functionName} call failed:`, error);
    throw error;
  }
};

// AI聊天API
const chatWithAI = async (
  message: string,
  context?: string,
  type: 'general' | 'model_discussion' | 'training_evaluation' | 'reference_answer' = 'general',
  modelNames?: string[]
): Promise<{ response: string }> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/ai-chat`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message,
        context,
        type,
        modelNames,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('AI聊天请求失败:', error);
    throw error;
  }
};

// 流式AI聊天API
export const chatWithAIStream = async (
  message: string,
  context?: string,
  type: 'general' | 'model_discussion' | 'training_evaluation' | 'reference_answer' = 'general',
  modelNames?: string[],
  onChunk?: (chunk: string) => void,
  onComplete?: (fullResponse: string) => void,
  onError?: (error: string) => void
) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/ai-chat`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message,
        context,
        type,
        modelNames,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let fullResponse = '';

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        console.log('Stream completed, full response:', fullResponse);
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      console.log('Received chunk:', chunk);
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.trim() === '') continue;
        
        try {
          const data = JSON.parse(line);
          console.log('Parsed data:', data);
          
          if (data.type === 'content') {
            fullResponse += data.data;
            onChunk?.(data.data);
          } else if (data.type === 'done') {
            console.log('Received done signal');
            onComplete?.(fullResponse);
            return { response: fullResponse };
          } else if (data.type === 'error') {
            console.error('Received error:', data.data);
            onError?.(data.data);
            throw new Error(data.data);
          }
        } catch (e) {
          console.warn('Parse error for line:', line, 'Error:', e);
        }
      }
    }

    onComplete?.(fullResponse);
    return { response: fullResponse };
  } catch (error) {
    console.error('流式AI聊天请求失败:', error);
    onError?.(error instanceof Error ? error.message : '请求失败');
    throw error;
  }
};

// 语音转文本API
const speechToText = async (audioFile: File) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    const formData = new FormData();
    formData.append('file', audioFile);

    const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/public-speech-to-text`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('语音转文本请求失败:', error);
    throw error;
  }
};

// 生成训练题目API
export const generateTrainingQuestion = async (
  contentData: string,
  contentName: string,
  modelNames: string[],
  topicTags: string[],
  thinkingTypeTags: string[]
) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-training-ai-generate-questions`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contentData,
        contentName,
        modelNames,
        topicTags,
        thinkingTypeTags,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('生成训练题目API响应:', data);
    return data;
  } catch (error) {
    console.error('生成训练题目请求失败:', error);
    throw error;
  }
};

// 评价答案API
export const evaluateAnswer = async (
  userAnswer: string,
  questionTitle: string,
  referenceAnswer: string,
  contentContext?: string
) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/evaluate-answer`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userAnswer,
        questionTitle,
        referenceAnswer,
        contentContext,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('评价答案请求失败:', error);
    throw error;
  }
};

// 训练会话管理API
export const manageTrainingSession = async (
  action: 'create' | 'update' | 'pause' | 'resume' | 'complete' | 'get' | 'list',
  sessionId?: string,
  sessionData?: any
) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-training-user-manage-sessions`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action,
        session_id: sessionId,
        session_data: sessionData,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('训练会话管理请求失败:', error);
    throw error;
  }
};

// 获取用户分析API
export const fetchUserAnalysis = async (userId: string, articleId: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/user-analysis?user_id=${userId}&article_id=${articleId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null; // No analysis found
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('获取用户分析请求失败:', error);
    throw error;
  }
};

// 提交用户分析API
export const postUserAnalysis = async (userId: string, articleId: string, formData: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/user-analysis`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        article_id: articleId,
        analysis_data: formData,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('提交用户分析请求失败:', error);
    throw error;
  }
};

// ==================== 数据管理API ====================

// 训练记录管理
export const manageTrainingRecords = async (
  action: 'create' | 'read' | 'update' | 'delete' | 'list',
  data?: any
) => {
  const method = action === 'list' || action === 'read' ? 'GET' : 
                action === 'create' ? 'POST' :
                action === 'update' ? 'PUT' : 'DELETE';
  
  const queryParams: Record<string, string> = { module: 'training-records' };
  
  if (action === 'list' || action === 'read') {
    if (data) {
      Object.keys(data).forEach(key => {
        if (data[key] !== undefined && data[key] !== null) {
          queryParams[key] = String(data[key]);
        }
      });
    }
    queryParams.action = action;
    return callFunction('user-data-manage', method, null, queryParams);
  } else {
    return callFunction('user-data-manage', method, { action, ...data }, queryParams);
  }
};

// AI会话管理
export const manageAISessions = async (
  action: 'create' | 'read' | 'update' | 'delete' | 'list',
  data?: any
) => {
  const method = action === 'list' || action === 'read' ? 'GET' : 
                action === 'create' ? 'POST' :
                action === 'update' ? 'PUT' : 'DELETE';
  
  const queryParams: Record<string, string> = { module: 'ai-sessions' };
  
  if (action === 'list') {
    queryParams.action = 'list';
    if (data?.page) queryParams.page = data.page.toString();
    if (data?.limit) queryParams.limit = data.limit.toString();
    if (data?.search) queryParams.search = data.search;
    if (data?.user_id) queryParams.user_id = data.user_id;
  } else if (action === 'read') {
    queryParams.action = 'detail';
    if (data?.id) queryParams.id = data.id;
  } else if (action === 'delete') {
    queryParams.action = 'delete';
    if (data?.id) queryParams.id = data.id;
  }
  
  const body = (action === 'create' || action === 'update') ? { action, ...data } : undefined;
  
  return callFunction('user-data-manage', method, body, queryParams);
};

// AI聊天管理
export const manageAIChats = async (
  action: 'create' | 'read' | 'update' | 'delete' | 'list',
  data?: any
) => {
  const method = action === 'list' || action === 'read' ? 'GET' : 
                action === 'create' ? 'POST' :
                action === 'update' ? 'PUT' : 'DELETE';
  
  const queryParams: Record<string, string> = { module: 'ai-chats' };
  
  if (action === 'list') {
    queryParams.action = 'list';
    if (data?.page) queryParams.page = data.page.toString();
    if (data?.limit) queryParams.limit = data.limit.toString();
    if (data?.search) queryParams.search = data.search;
    if (data?.session_id) queryParams.session_id = data.session_id;
    if (data?.user_id) queryParams.user_id = data.user_id;
  } else if (action === 'read') {
    queryParams.action = 'detail';
    if (data?.id) queryParams.id = data.id;
  } else if (action === 'delete') {
    queryParams.action = 'delete';
    if (data?.id) queryParams.id = data.id;
  }
  
  const body = (action === 'create' || action === 'update') ? { action, ...data } : undefined;
  
  return callFunction('user-data-manage', method, body, queryParams);
};

// 诊断会话管理
export const manageDiagnosisSessions = async (
  action: 'create' | 'read' | 'update' | 'delete' | 'list',
  data?: any
) => {
  return callFunction('diagnosis-unified-manage', 'POST', { action, module: 'sessions', ...data });
};

// 诊断答案管理
export const manageDiagnosisAnswers = async (
  action: 'create' | 'read' | 'update' | 'delete' | 'list',
  data?: any
) => {
  return callFunction('diagnosis-unified-manage', 'POST', { action, module: 'answers', ...data });
};

// 内容管理
export const manageContents = async (
  action: 'create' | 'read' | 'update' | 'delete' | 'list',
  data?: any
) => {
  return callFunction('edu-content-admin-manage', 'POST', { action, ...data });
};

// 模型管理
export const manageModels = async (
  action: 'create' | 'read' | 'update' | 'delete' | 'list' | 'get',
  data?: any
) => {
  // 根据 action 选择合适的 HTTP 方法
  let method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'POST';
  let queryParams: Record<string, string> | undefined;
  
  if (action === 'get' && data?.model_id) {
    method = 'GET';
    queryParams = { action: 'get', model_id: data.model_id };
    return callFunction('models-admin-manage', method, undefined, queryParams);
  } else if (action === 'list') {
    method = 'GET';
    queryParams = { action: 'list' };
    if (data?.status) queryParams.status = data.status;
    if (data?.category) queryParams.category = data.category;
    if (data?.limit) queryParams.limit = data.limit.toString();
    if (data?.offset) queryParams.offset = data.offset.toString();
    if (data?.search) queryParams.search = data.search;
    return callFunction('models-admin-manage', method, undefined, queryParams);
  } else if (action === 'update') {
    method = 'PUT';
    return callFunction('models-admin-manage', method, { action, ...data });
  } else if (action === 'delete' && data?.model_id) {
    method = 'DELETE';
    queryParams = { action: 'delete', model_id: data.model_id };
    return callFunction('models-admin-manage', method, undefined, queryParams);
  }
  
  return callFunction('models-admin-manage', method, { action, ...data });
};

// 获取模型详情 - 专用函数
export const getModelDetail = async (modelId: string): Promise<StandardResponse<any>> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/models-admin-manage?action=detail&id=${modelId}`, {
      method: 'GET',
      headers: {
        'Authorization': session?.access_token ? `Bearer ${session.access_token}` : '',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('获取模型详情失败:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '获取模型详情失败'
    };
  }
};

// 获取公开发布的模型列表
export const fetchPublishedModels = async (params?: {
  search?: string;
  category?: string;
  tag?: string;
  limit?: number;
  offset?: number;
}): Promise<StandardResponse<any>> => {
  try {
    const searchParams = new URLSearchParams({ action: 'list' });
    if (params?.search) searchParams.append('search', params.search);
    if (params?.category) searchParams.append('category', params.category);
    if (params?.tag) searchParams.append('tag', params.tag);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());

    const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/public-models?${searchParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'apikey': API_CONFIG.SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${API_CONFIG.SUPABASE_ANON_KEY}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('获取公开模型失败:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '获取模型失败'
    };
  }
};

// 获取公开发布的模型详情
export const fetchPublishedModelDetail = async (modelId: string): Promise<StandardResponse<any>> => {
  try {
    const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/public-models?action=detail&id=${modelId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'apikey': API_CONFIG.SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${API_CONFIG.SUPABASE_ANON_KEY}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('获取模型详情失败:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '获取模型详情失败'
    };
  }
};

// 模型交互管理
export const manageModelInteractions = async (
  action: 'chat_history' | 'save_chat' | 'save_notes',
  modelId: string,
  data?: any
): Promise<StandardResponse<any>> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      return {
        success: false,
        error: '需要登录才能访问'
      };
    }

    if (action === 'chat_history') {
      // 获取聊天历史 - 使用 model_chats 表
      const queryParams = new URLSearchParams({
        module: 'model-chats',
        action: 'list',
        model_id: modelId
      });
      
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/user-learning-unified-manage?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return {
        success: responseData.success,
        data: { chat_history: responseData.data || [] },
        error: responseData.error
      };
      
    } else if (action === 'save_chat') {
      // 保存聊天记录 - 直接使用 Supabase 客户端插入到 model_chats 表
      const { data: insertData, error } = await supabase
        .from('model_chats')
        .insert({
          model_id: modelId,
          user_question: data.user_question,
          ai_answer: data.ai_answer
        });

      if (error) {
        console.error('保存聊天记录失败:', error);
        return {
          success: false,
          error: error.message
        };
      }

      return {
        success: true,
        data: insertData
      };
      
    } else if (action === 'save_notes') {
      // 保存笔记 - 使用 model_study_records 表
      // 先检查是否已有记录，如果有则更新，没有则创建
      const { data: existingRecord } = await supabase
        .from('model_study_records')
        .select('*')
        .eq('model_id', modelId)
        .eq('user_id', session.user.id)
        .single();

      let result;
      if (existingRecord) {
        // 更新现有记录
        const { data: updateData, error } = await supabase
          .from('model_study_records')
          .update({ notes: data.notes })
          .eq('id', existingRecord.id);
        
        if (error) {
          return { success: false, error: error.message };
        }
        result = updateData;
      } else {
        // 创建新记录
        const { data: insertData, error } = await supabase
          .from('model_study_records')
          .insert({
            model_id: modelId,
            notes: data.notes
          });
        
        if (error) {
          return { success: false, error: error.message };
        }
        result = insertData;
      }

      return {
        success: true,
        data: result
      };
    }

    return {
      success: false,
      error: '不支持的操作'
    };
    
  } catch (error) {
    console.error('模型交互管理失败:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '操作失败'
    };
  }
};

// 训练题目管理 (管理员使用)
export const manageTrainingQuestions = async (
  action: 'create' | 'read' | 'update' | 'delete' | 'list',
  data?: any
) => {
  return callFunction('edu-training-admin-manage-questions', 'POST', { action, ...data });
};

// 获取训练题目 (公共接口，普通用户可以使用)
export const getTrainingQuestions = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  topic_tag?: string;
  thinking_tag?: string;
}): Promise<StandardResponse<any[]>> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.topic_tag) searchParams.append('topic_tag', params.topic_tag);
    if (params?.thinking_tag) searchParams.append('thinking_tag', params.thinking_tag);
    
    const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
    
    const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-training-public-get-questions${query}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // 确保返回标准化的响应格式
    if (data.success && data.data && Array.isArray(data.data.data)) {
      return {
        success: true,
        data: data.data.data,
        metadata: data.data.pagination
      };
    } else {
      return {
        success: false,
        error: data.error || '获取训练题目失败',
        data: []
      };
    }
  } catch (error) {
    console.error('Get training questions failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '获取训练题目失败',
      data: []
    };
  }
};

// 标签管理
export const manageTags = async (
  action: 'create' | 'read' | 'update' | 'delete' | 'list',
  data?: any
) => {
  return callFunction('edu-tags-unified-manage', 'POST', { mode: 'public', action, ...data });
};

// 诊断模板管理
export const manageDiagnosisTemplates = async (
  action: 'create' | 'read' | 'update' | 'delete' | 'list',
  data?: any
) => {
  return callFunction('diagnosis-unified-manage', 'POST', { action, module: 'templates', ...data });
};

// 新闻文章管理
export const manageNewsArticles = async (
  action: 'create' | 'read' | 'update' | 'delete' | 'list',
  data?: any
) => {
  return callFunction('news-admin-manage', 'POST', { action, module: 'articles', ...data });
};

// 官方分析管理
export const manageOfficialAnalysis = async (
  action: 'create' | 'read' | 'update' | 'delete' | 'list',
  data?: any
) => {
  return callFunction('news-admin-manage', 'POST', { action, module: 'analysis', ...data });
};



// 模型聊天记录管理
export const manageModelChats = async (
  action: 'create' | 'read' | 'update' | 'delete' | 'list',
  data?: any
) => {
  return callFunction('manage-ai-chats', 'POST', { action, resource_type: 'model_chats', ...data });
};
